import { useState, useEffect } from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from './useAuth';
import { useToast } from '@/components/ui/use-toast';
import type { SupportTicket, TicketReply } from '@/types/invoice';

export function useTickets() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [activeTicket, setActiveTicket] = useState<SupportTicket | null>(null);
  const [replies, setReplies] = useState<TicketReply[]>([]);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Listen to all tickets for the authenticated user
  useEffect(() => {
    if (!user) {
      setTickets([]);
      setActiveTicket(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const q = query(
      collection(db, 'tickets'),
      where('userId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const ticketsList = snapshot.docs
          .map((docSnap) => {
            const data = docSnap.data();
            return {
              ...data,
              ticketId: docSnap.id,
              createdAt: data.createdAt?.toDate(),
              updatedAt: data.updatedAt?.toDate(),
            } as SupportTicket;
          })
          .sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));

        setTickets(ticketsList);
        
        // Find if there is an active open ticket
        const openTicket = ticketsList.find((t) => t.status === 'open') || null;
        setActiveTicket(openTicket);

        // Auto-select active ticket if none is selected, or if the selected ticket is not in list
        if (openTicket) {
          setSelectedTicketId(openTicket.ticketId);
        }

        setLoading(false);
      },
      (error) => {
        console.error('Error fetching user tickets:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Listen to replies for the selected ticket
  useEffect(() => {
    if (!selectedTicketId) {
      setReplies([]);
      return;
    }

    const q = query(
      collection(db, 'tickets', selectedTicketId, 'replies'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const repliesList = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            ...data,
            replyId: docSnap.id,
            createdAt: data.createdAt?.toDate(),
          } as TicketReply;
        });
        setReplies(repliesList);
      },
      (error) => {
        console.error('Error fetching ticket replies:', error);
      }
    );

    return () => unsubscribe();
  }, [selectedTicketId]);

  // Create a new support ticket
  const createTicket = async (title: string, description: string, screenshotUrl?: string) => {
    if (!user) throw new Error('User not authenticated');

    const openTicket = tickets.find((t) => t.status === 'open');
    if (openTicket) {
      toast({
        title: 'Open Ticket Exists',
        description: 'You already have an open ticket. Please wait until it is closed.',
        variant: 'destructive',
      });
      throw new Error('An open ticket already exists');
    }

    setSubmitting(true);
    try {
      const docRef = await addDoc(collection(db, 'tickets'), {
        userId: user.uid,
        userEmail: user.email || '',
        title,
        description,
        screenshotUrl: screenshotUrl || null,
        status: 'open',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      setSelectedTicketId(docRef.id);
      toast({
        title: 'Ticket Submitted',
        description: 'Support ticket has been created successfully.',
      });
      return docRef.id;
    } catch (error: any) {
      console.error('Error creating ticket:', error);
      toast({
        title: 'Submission Failed',
        description: error.message || 'Could not submit support ticket.',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  // Reply to ticket
  const replyToTicket = async (ticketId: string, text: string, isAdmin = false) => {
    if (!user) throw new Error('User not authenticated');
    if (!text.trim()) return;

    setSubmitting(true);
    try {
      await addDoc(collection(db, 'tickets', ticketId, 'replies'), {
        senderId: user.uid,
        senderEmail: user.email || '',
        isAdmin,
        text: text.trim(),
        createdAt: Timestamp.now(),
      });

      await updateDoc(doc(db, 'tickets', ticketId), {
        updatedAt: Timestamp.now(),
      });
    } catch (error: any) {
      console.error('Error replying to ticket:', error);
      toast({
        title: 'Reply Failed',
        description: 'Could not send comment.',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  // Close ticket
  const closeTicket = async (ticketId: string) => {
    if (!user) throw new Error('User not authenticated');

    setSubmitting(true);
    try {
      await updateDoc(doc(db, 'tickets', ticketId), {
        status: 'closed',
        updatedAt: Timestamp.now(),
      });

      toast({
        title: 'Ticket Closed',
        description: 'Support ticket status set to closed.',
      });
    } catch (error: any) {
      console.error('Error closing ticket:', error);
      toast({
        title: 'Error',
        description: 'Failed to close the ticket.',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  return {
    tickets,
    activeTicket,
    replies,
    selectedTicketId,
    setSelectedTicketId,
    loading,
    submitting,
    createTicket,
    replyToTicket,
    closeTicket,
  };
}
